<template>
  <div>
    <div class="toolbar">
      <el-form inline class="filters">
        <el-input v-model.trim="filters.keyword" class="task-keyword" placeholder="任务编号" clearable @keyup.enter.native="search"/>
        <el-select v-model="filters.status" class="task-status" clearable placeholder="全部状态">
          <el-option v-for="item in statuses" :key="item.value" :label="item.label" :value="item.value"/>
        </el-select>
        <el-date-picker v-model="filters.dates" class="task-dates" type="daterange" value-format="yyyy-MM-dd" range-separator="至" start-placeholder="开始日期" end-placeholder="结束日期" clearable/>
        <el-button type="primary" :loading="loading" @click="search">{{loading?'查询中':'查询'}}</el-button>
        <el-button :disabled="loading" @click="reset">重置</el-button>
      </el-form>
      <el-button :loading="loading" @click="load">刷新</el-button>
    </div>

    <el-table :data="items" v-loading="loading" empty-text="暂无符合条件的任务">
      <el-table-column prop="id" label="任务编号" min-width="280" show-overflow-tooltip/>
      <el-table-column label="创建时间" width="165"><template slot-scope="s">{{ formatTime(s.row.created_at) }}</template></el-table-column>
      <el-table-column label="完成时间" width="165"><template slot-scope="s">{{ s.row.status === 'running' ? '进行中' : formatTime(s.row.finished_at) }}</template></el-table-column>
      <el-table-column label="状态" width="110"><template slot-scope="s"><el-tag :type="statusType(s.row.status)">{{ statusLabel(s.row.status) }}</el-tag></template></el-table-column>
      <el-table-column label="阶段" width="100"><template slot-scope="s">{{ stageLabel(s.row.stage) }}</template></el-table-column>
      <el-table-column label="结果摘要" min-width="200"><template slot-scope="s">{{ s.row.stop_reason || '-' }}</template></el-table-column>
      <el-table-column label="操作" width="150">
        <template slot-scope="s">
          <el-button type="text" :loading="detailLoading && detailRunId === s.row.id" :disabled="detailLoading" @click="openDetail(s.row)">{{ detailLoading && detailRunId === s.row.id ? '加载中' : '查看详情' }}</el-button>
          <el-button v-if="s.row.status === 'running'" type="text" class="danger" :loading="stoppingId===s.row.id" :disabled="!!stoppingId" @click="stop(s.row)">{{stoppingId===s.row.id?'停止中':'停止'}}</el-button>
        </template>
      </el-table-column>
    </el-table>
    <div v-if="total" class="pagination"><el-pagination background layout="total, prev, pager, next, jumper" :disabled="loading" :current-page="page" :page-size="pageSize" :total="total" @current-change="changePage"/></div>

    <el-drawer title="任务详情" :visible="detailVisible" size="72%" @close="detailVisible = false">
      <div class="detail" v-loading="detailLoading">
        <el-alert v-if="timeline" class="detail-alert" :type="conclusionType(timeline.conclusion)" :title="timeline.conclusion_message" :closable="false" show-icon/>
        <el-descriptions v-if="detail" :column="2" border>
          <el-descriptions-item label="任务编号" :span="2">{{ detail.run.id }}</el-descriptions-item>
          <el-descriptions-item label="状态">{{ statusLabel(detail.run.status) }}</el-descriptions-item>
          <el-descriptions-item label="阶段">{{ stageLabel(detail.run.stage) }}</el-descriptions-item>
          <el-descriptions-item label="触发方式">{{ triggerLabel(timeline && timeline.trigger_type) }}</el-descriptions-item>
          <el-descriptions-item label="无/低风险">{{ timeline ? `${timeline.screening_counts.safe} 无 / ${timeline.screening_counts.low} 低` : '-' }}</el-descriptions-item>
          <el-descriptions-item label="中高风险">{{ timeline ? `${timeline.risk_counts.medium} 中 / ${timeline.risk_counts.high} 高` : '-' }}</el-descriptions-item>
          <el-descriptions-item label="失败原因" :span="2">{{ detail.run.stop_reason || '-' }}</el-descriptions-item>
        </el-descriptions>
        <h4>运行时间轴</h4>
        <el-timeline v-if="timeline && timeline.events && timeline.events.length">
          <el-timeline-item v-for="(event, index) in timeline.events" :key="index" :timestamp="formatTime(event.time)" :type="eventType(event.level)">
            <div class="event-title">{{ event.title }}</div>
            <div v-if="event.description" class="event-description">{{ event.description }}</div>
            <el-button v-if="event.decision_traces && event.decision_traces.length" type="text" size="mini" @click="showDecisionTraces(event)">查看无/低风险留痕（{{ event.decision_traces.length }}）</el-button>
            <el-button v-if="event.diagnostic" type="text" size="mini" @click="showDiagnostic(event)">查看诊断详情</el-button>
          </el-timeline-item>
        </el-timeline>
        <el-empty v-else description="暂无运行事件"/>
        <h4>平台执行情况</h4>
        <el-table :data="detail ? detail.platform_runs || [] : []" empty-text="暂无平台执行明细">
          <el-table-column prop="platform" label="平台"><template slot-scope="s">{{ platformName(s.row.platform) }}</template></el-table-column>
          <el-table-column prop="status" label="状态"><template slot-scope="s">{{ statusLabel(s.row.status) }}</template></el-table-column>
          <el-table-column prop="stage" label="阶段"><template slot-scope="s">{{ stageLabel(s.row.stage) }}</template></el-table-column>
          <el-table-column prop="failure_code" label="错误类型" min-width="180">
            <template slot-scope="s"><el-tag v-if="s.row.failure_code" type="danger" size="mini">{{ failureLabel(s.row.failure_code) }}</el-tag><span v-else>-</span></template>
          </el-table-column>
          <el-table-column prop="error_summary" label="失败原因" min-width="320"><template slot-scope="s">{{ s.row.error_summary || '-' }}</template></el-table-column>
        </el-table>
      </div>
    </el-drawer>
    <el-dialog title="采集诊断详情" :visible.sync="diagnosticVisible" width="640px">
      <pre class="diagnostic-content">{{ diagnosticText }}</pre>
      <span slot="footer"><el-button @click="copyDiagnostic">复制详情</el-button><el-button type="primary" @click="diagnosticVisible = false">关闭</el-button></span>
    </el-dialog>
    <el-dialog :title="decisionTraceTitle" :visible.sync="decisionTraceVisible" width="760px">
      <el-alert title="这里只保留内容ID和AI判断摘要，未保存原文、作者、链接或评论。" type="info" :closable="false" show-icon/>
      <el-table class="decision-trace-table" :data="decisionTraces" empty-text="暂无判断留痕">
        <el-table-column prop="source_id" label="内容ID" min-width="180" show-overflow-tooltip/>
        <el-table-column label="判断" width="90"><template slot-scope="s"><el-tag :type="s.row.decision === 'low_risk' ? 'warning' : 'success'" size="mini">{{ decisionLabel(s.row.decision) }}</el-tag></template></el-table-column>
        <el-table-column prop="conclusion" label="判断摘要" min-width="300"/>
        <el-table-column label="方式" width="80"><template slot-scope="s">{{ s.row.reused ? '复用' : '新判断' }}</template></el-table-column>
      </el-table>
    </el-dialog>
  </div>
</template>

<script>
import { yuqingApi } from '@/api/yuqing'

function today () {
  const date = new Date()
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

export default {
  data () {
    return {
      items: [], total: 0, page: 1, pageSize: 20, loading: false, stoppingId: '', detail: null, timeline: null, detailLoading: false, detailRunId: '', detailVisible: false, diagnosticVisible: false, diagnosticText: '',
      decisionTraceVisible: false, decisionTraceTitle: '无/低风险判断留痕', decisionTraces: [],
      filters: { keyword: '', status: '', dates: [today(), today()] },
      statuses: [{ value: 'running', label: '运行中' }, { value: 'completed', label: '已完成' }, { value: 'partial_failed', label: '部分失败' }, { value: 'failed', label: '失败' }, { value: 'budget_exhausted', label: '额度用完' }, { value: 'not_executed', label: '未执行' }, { value: 'stopped', label: '已停止' }]
    }
  },
  mounted () { this.load() },
  methods: {
    async load () {
      this.loading = true
      try {
        const params = { currentPage: this.page, pageSize: this.pageSize }
        if (this.filters.keyword) params.keyword = this.filters.keyword
        if (this.filters.status) params.status = this.filters.status
        if (this.filters.dates && this.filters.dates.length === 2) { params.startAt = this.filters.dates[0]; params.endAt = this.filters.dates[1] }
        const result = await yuqingApi.get('runs', params)
        this.items = result.items || []
        this.total = result.total || 0
      } catch (error) { this.$message.error(error.message) } finally { this.loading = false }
    },
    search () { if (this.loading) return; this.page = 1; return this.load() },
    reset () { if (this.loading) return; this.filters = { keyword: '', status: '', dates: [today(), today()] }; return this.search() },
    changePage (page) { this.page = page; this.load() },
    async openDetail (row) {
      if (this.detailLoading) return
      this.detailVisible = true
      this.detail = null
      this.timeline = null
      this.detailLoading = true
      this.detailRunId = row.id
      try {
        const result = await Promise.all([yuqingApi.get(`runs/${encodeURIComponent(row.id)}`), yuqingApi.get(`runs/${encodeURIComponent(row.id)}/timeline`)])
        this.detail = result[0]
        this.timeline = result[1]
      } catch (error) { this.$message.error(error.message) } finally { this.detailLoading = false; this.detailRunId = '' }
    },
    async stop (row) {
      if (this.stoppingId) return
      try {
        await this.$confirm('停止后当前任务不会继续采集。确定停止吗？', '停止任务', { type: 'warning' })
        this.stoppingId = row.id
        await yuqingApi.post(`runs/${encodeURIComponent(row.id)}/stop`, {})
        this.$message.success('任务已停止')
        await this.load()
      } catch (error) { if (error !== 'cancel' && error !== 'close') this.$message.error(error.message || '停止任务失败') } finally { this.stoppingId = '' }
    },
    statusLabel (status) { return ({ running: '运行中', completed: '已完成', partial_failed: '部分失败', failed: '失败', stopped: '已停止', budget_exhausted: '额度用完', not_executed: '未执行' })[status] || status || '-' },
    statusType (status) { return ({ running: 'warning', completed: 'success', partial_failed: 'warning', failed: 'danger', budget_exhausted: 'danger', not_executed: 'info', stopped: 'info' })[status] || 'info' },
    conclusionType (value) { return ({ risk_found: 'error', low_risk_observed: 'warning', no_risk_found: 'success', running: 'info', incomplete: 'warning', not_executed: 'warning' })[value] || 'info' },
    triggerLabel (value) { return ({ manual: '手动验证', scheduled: '定时采集', retry: '补偿采集' })[value] || '-' },
    eventType (level) { return ({ success: 'success', warning: 'warning', error: 'danger', info: 'primary' })[level] || 'info' },
    platformName (value) { return ({ bilibili: '哔哩哔哩', douyin: '抖音', kuaishou: '快手', wechat_mp: '微信公众号', weibo: '微博' })[value] || value || '-' },
    showDiagnostic (event) { this.diagnosticText = JSON.stringify(event.diagnostic, null, 2); this.diagnosticVisible = true },
    showDecisionTraces (event) { this.decisionTraces = event.decision_traces || []; this.decisionTraceTitle = `${event.platform_name || '平台'}无/低风险判断留痕`; this.decisionTraceVisible = true },
    decisionLabel (value) { return value === 'low_risk' ? '低风险' : '无风险' },
    async copyDiagnostic () { try { await navigator.clipboard.writeText(this.diagnosticText); this.$message.success('诊断详情已复制') } catch (error) { this.$message.warning('复制失败，请手动选择文本复制') } },
    stageLabel (stage) { return ({ search: '搜索', report: '生成报告', stopped: '已停止', budget: '额度检查', quota: '额度保护' })[stage] || stage || '-' },
    failureLabel (code) {
      return ({
        DAILY_LIMIT_EXCEEDED: '今日额度已用完',
        QUOTA_REDIS_UNAVAILABLE: 'Redis额度服务不可用',
        QUOTA_REDIS_INVALID_RESULT: 'Redis额度结果异常',
        QUOTA_INITIALIZATION_FAILED: '当日额度初始化失败',
        REQUEST_LOG_WRITE_FAILED: '请求日志预占失败'
      })[code] || code
    },
    formatTime (value) { return value ? String(value).replace('T', ' ').slice(0, 19) : '-' }
  }
}
</script>

<style scoped>
.toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.filters { margin-bottom: 0; }
.task-keyword { width: 260px; }
.task-status { width: 130px; }
.task-dates { width: 260px; }
.pagination { display: flex; justify-content: flex-end; margin-top: 16px; }
.detail { padding: 0 20px 20px; }
.detail-alert { margin-bottom: 16px; }
.event-title { color: #303133; font-weight: 500; }
.event-description { margin-top: 4px; color: #606266; line-height: 1.6; }
.decision-trace-table { margin-top: 14px; }
.diagnostic-content { max-height: 420px; margin: 0; padding: 14px; overflow: auto; color: #303133; background: #f5f7fa; border-radius: 4px; white-space: pre-wrap; word-break: break-all; }
.danger { color: #f56c6c; }
</style>
